import React, { useEffect, useState, useRef } from 'react';
import { SummarizerConfig } from '../../../types/summarizer';
import InputSection from './input-section';
import OutputTabs from './output-tabs';
import SummarizerLayout from './SummarizerLayout';
import CTADialog from '../../ui/CTADialog';
import { useSummarizer } from '@core/store/useSummarizer';
import { getCTAContent, getInitialSlideIndex } from '../../../config/cta-content';
import { ErrorAlert } from '../../ui';
import { cn } from '@/components/lib/utils';

interface UniversalSummarizerProps {
  config: SummarizerConfig;
  onBack?: () => void;
  onComplete?: (results: any) => void;
  onError?: (error: any) => void;
  primaryColor?: string;
  className?: string;
}

const UniversalSummarizer: React.FC<UniversalSummarizerProps> = ({
  config,
  className = '',
  onComplete,
  onError,
}) => {
  const [localError, setLocalError] = useState<string | null>(null);
  const {
    currentSummary,
    error: storeError,
    quota,
    fetchQuota,
    reset,
    showCTADialog,
    ctaDialogType,
    ctaErrorMessage,
    ctaFeatureContext,
    setShowCTADialog,
    setCTADialogType,
    setCustomPrompt,
    setError: setStoreError,
  } = useSummarizer();

  const outputRef = useRef<HTMLDivElement>(null);

  // Load custom components if specified in config
  const CustomInputComponent = config.ui?.customComponents?.input;
  const CustomOutputComponent = config.ui?.customComponents?.output;
  const CustomLayoutComponent = config.ui?.customComponents?.layout;

  // Use custom layout if provided, otherwise default
  const LayoutComponent = CustomLayoutComponent || SummarizerLayout;

  // Clear error when config changes
  useEffect(() => {
    reset();
  }, [config.id]);

  useEffect(() => {
    fetchQuota();
  }, []);

  // Set custom prompt from config if provided
  useEffect(() => {
    if (config.prompt) {
      setCustomPrompt(config.prompt);
    }
  }, [config.prompt, setCustomPrompt]);

  // Handle completion callback and scroll to output
  useEffect(() => {
    if (currentSummary) {
      // Scroll to output section when summary is generated
      setTimeout(() => {
        if (outputRef.current) {
          outputRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 100); // Small delay to ensure DOM is updated

      if (onComplete) {
        onComplete(currentSummary);
      }
    }
  }, [currentSummary, onComplete]);

  // Handle error callback
  useEffect(() => {
    if ((localError || storeError) && onError) {
      onError(localError || storeError);
    }
  }, [localError, storeError, onError]);

  /**
   * Clear results and return to input
   */
  const handleClearResults = () => {
    setLocalError(null);
    setStoreError(null);
  };

  // Dynamic CTA logic for store errors (especially transcript errors)
  const getStoreErrorCTA = () => {
    const isLoggedIn = Boolean(quota?.user_id);

    if (!isLoggedIn) {
      return {
        text: 'Sign Up to Continue',
        onClick: () => {
          setCTADialogType('sign_up_required');
          setShowCTADialog(true);
        },
        variant: 'primary' as const,
      };
    }

    // If user is logged in but quota exceeded
    if (quota && quota.remaining_tokens <= 0) {
      return {
        text: 'Upgrade Plan',
        onClick: () => {
          setCTADialogType('quota_exceeded');
          setShowCTADialog(true);
        },
        variant: 'primary' as const,
      };
    }

    // Default fallback for logged in users
    return {
      text: 'Contact Support',
      href: 'mailto:support@tubeonai.com',
      variant: 'secondary' as const,
      external: true,
    };
  };

  // Determine primary color from config
  const primaryColor = config.ui?.primaryColor || '#3b82f6';

  return (
    <LayoutComponent config={config} className={className}>
      {/* Tool Header and Input Section */}
      <>
        {/* Input Section - Use custom component if provided, otherwise default */}
        {CustomInputComponent ? (
          <CustomInputComponent config={config} />
        ) : (
          <>
            <InputSection
              config={config.inputs}
              primaryColor={primaryColor}
              ui={config.ui}
              fullConfig={config}
            />
          </>
        )}
      </>

      {/* Store Error Display - Show if there's a store error but no summary */}
      {storeError && !currentSummary && (
        <div className="mt-4">
          <ErrorAlert
            type="warning"
            title="Content Not Available"
            message={storeError}
            actionButton={getStoreErrorCTA()}
            onDismiss={handleClearResults}
            className={cn(config.id === 'text-summarizer' && 'm-4 box-content')}
          />
        </div>
      )}

      {CustomOutputComponent ? (
        <CustomOutputComponent
          summary={currentSummary}
          config={config}
          onBack={handleClearResults}
        />
      ) : currentSummary ? (
        <div ref={outputRef}>
          <OutputTabs
            summary={currentSummary}
            config={config}
            onBack={handleClearResults}
            primaryColor={primaryColor}
          />
        </div>
      ) : null}

      {/* CTA Dialog */}
      <CTADialog
        isOpen={showCTADialog}
        onClose={() => setShowCTADialog(false)}
        content={getCTAContent(ctaDialogType)}
        initialSlide={getInitialSlideIndex(
          getCTAContent(ctaDialogType),
          ctaFeatureContext || undefined
        )}
        errorMessage={ctaErrorMessage || undefined}
        featureContext={ctaFeatureContext || undefined}
      />
    </LayoutComponent>
  );
};

export default UniversalSummarizer;
